import { ApolloProvider } from '@apollo/client';
import StructuredData from '@components/StructuredData';
import { library } from '@fortawesome/fontawesome-svg-core';
import { far } from '@fortawesome/free-regular-svg-icons';
import {
    FETCH_BLOG_BY_SLUG,
    FETCH_RELATED_BLOGS,
    FETCH_FIRST_12_BLOGS_SLUG
} from '@services/blogs.service';
import { useRouter } from 'next/router';

import { Breadcrumbs } from '@atoms/index';
import {
    detailedBlogBreadcrumbs,
    getBreadCrumbs,
} from '@components/Blogs/helper';
import { regexPattern } from '@helpers/regex';
import client from '@lib/apollo-client';
import NextHeadTag from '@ui/molecules/NextHeadTag';
import dynamic from 'next/dynamic';
import {useEffect} from "react";
import {logEvent, moengageEventArgsConstants, moengageEventConstants} from "@services/moengage";
const BlogInfo = dynamic(() => import('@components/Blogs/BlogDetails'));
const RelatedBlogs = dynamic(() => import('@components/Blogs/RelatedBlogs'));
import { getBlogMetaKeywords, isValidJSON } from '@helpers/miscelleanous';

library.add(far);

const dummyBlogs = Array.from({ length: 3 }, (_, i) => ({ id: i }));

function BlogDetails({ slug, data, relatedBlogsResult, blogCategories }) {
    let metaTitleTag = data?.postBy?.tags?.nodes?.find((item) => item.name?.includes('title'));
    let metaDescriptionTag = data?.postBy?.tags?.nodes?.find((item) => item?.name?.includes('metaDescription'));
      
    metaTitleTag = metaTitleTag?.name && isValidJSON(metaTitleTag.name) ? JSON.parse(metaTitleTag.name) : null;
    metaDescriptionTag = metaDescriptionTag?.name && isValidJSON(metaDescriptionTag.name) ? JSON.parse(metaDescriptionTag.name) : null;

    const router = useRouter();
    const blogDetails = data?.postBy;
    const filteredRelatedBlogs = relatedBlogsResult?.posts?.nodes.filter(
        (blog) => !blog.slug.includes(router.query?.slug)
    );

    const relateBlogs = filteredRelatedBlogs?.length
        ? filteredRelatedBlogs
        : null;

    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: `${blogDetails?.title}`,
        url: `https://www.jiraaf.com/blogs/${router.query?.slug}`,
        author: {
            '@type': 'Person',
            name: `${blogDetails?.author?.node?.name}`,
        },
        datePublished: `${blogDetails?.date}`,
    };

    useEffect(() => {
        logEvent({
            eventName: moengageEventConstants.VIEWED_BLOGS_DETAIL_PAGE,
            parameters: {
                [moengageEventArgsConstants.BLOG_SLUG]: slug
            }
        });
    }, []);

    return (
        <>
            <NextHeadTag
                title={metaTitleTag?.title?.replace(/{comma}/g, ",")}
                canonicalUrl={`${process.env.NEXT_PUBLIC_APP_URL}/blogs/${slug}`}
                metaKeywords={getBlogMetaKeywords(
                    blogDetails?.title,
                    blogCategories
                )}
                metaDesc={metaDescriptionTag?.metaDescription?.replace(/{comma}/g, ",")}
            />

            <StructuredData data={structuredData} />
            <div className="blog-details px-4 md:px-12 lg:px-16 xl:px-28 pt-5 pb-10 bg-bgColor-50">
                <Breadcrumbs
                    children={getBreadCrumbs(
                        detailedBlogBreadcrumbs(blogDetails?.title)
                    )}
                    className="flex"
                    separator="/"
                />
                <div className="pt-3 xl:pt-8">
                    <BlogInfo
                        blogDetails={blogDetails}
                        categories={blogCategories}
                        relateBlogs={relateBlogs}
                    />
                    {(relateBlogs || relateBlogs?.length) && (
                        <RelatedBlogs
                            blogs={relateBlogs || dummyBlogs}
                            isLoading={false}
                        />
                    )}
                </div>
            </div>
        </>
    );
}

const BlogDetailsWrapper = ({ slug, data, relatedBlogsResult, blogCategories }) => (
    <ApolloProvider client={client}>
        <BlogDetails slug={slug} data={data} relatedBlogsResult={relatedBlogsResult} blogCategories={blogCategories} />
    </ApolloProvider>
);

export async function getStaticPaths() {
    const { data } = await client.query({
        query: FETCH_FIRST_12_BLOGS_SLUG,
    });

    const paths = data.posts.nodes.map((post) => ({
        params: { slug: post.slug },
    }));

    return {
        paths,
        // Using 'blocking' fallback mode ensures that for any paths not generated at build time, 
        // Next.js will server-render the page on demand (SSR) and cache the result for future requests.
        // This helps handle dynamic content while maintaining good performance for frequently accessed paths.
        fallback: 'blocking',
    };
}

// Function to validate and sanitize the slug
const validateAndSanitizeSlug = (slug) => {
    const isValidSlug = regexPattern.ALLOW_BLOGS_SLUG.test(slug);
    return isValidSlug ? slug : '';
};

const fetchBlogDetails = async (slug) => {
    const response = await client.query({
        query: FETCH_BLOG_BY_SLUG,
        variables: { slug },
        fetchPolicy: 'no-cache',
    });
    return response?.data || null;
};

// Function to fetch related blogs based on categories
const fetchRelatedBlogs = async (blogCategories) => {
    const response = await client.query({
        query: FETCH_RELATED_BLOGS,
        variables: { categories: blogCategories, first: 3 },
        fetchPolicy: 'no-cache',
    });
    return response?.data || null;
};

// Function to clone blog details and modify SEO-sensitive data
const cloneAndModifyBlogDetails = (blogDetails) => {
    if (!blogDetails) return null;
    
    let clonedBlogDetails = JSON.parse(JSON.stringify(blogDetails));
    if (clonedBlogDetails.postBy.featuredImage?.node?.uri) {
        clonedBlogDetails.postBy.featuredImage.node.uri = null; 
        clonedBlogDetails.postBy.featuredImage.node.link = null;
    }
    return clonedBlogDetails;
};

export async function getStaticProps(ctx) {
    const { slug } = ctx.params || {};
    const sanitizedSlug = validateAndSanitizeSlug(slug);

    try {
        if (!sanitizedSlug) throw Error('Invalid Slug');
        const blogDetails = await fetchBlogDetails(sanitizedSlug);
        const blogCategories = blogDetails?.postBy?.categories?.edges?.map((edge) => edge.node.name).join(', ');
        const relatedBlogs = await fetchRelatedBlogs(blogCategories);
        const clonedBlogDetails = cloneAndModifyBlogDetails(blogDetails);

        return {
            props: {
                slug: sanitizedSlug,
                data: clonedBlogDetails,
                relatedBlogsResult: relatedBlogs,
                blogCategories: blogCategories,
            },
        };
    } catch (error) {
        return {
            notFound: true,
        };
    }
}

export default BlogDetailsWrapper;
